// ============================================================
// ByOurConsent.com -- Article Upload Page
// For admins and contributors to upload .docx articles
// ============================================================

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ArticleUploadPage = () => {
    const { user, isAdmin, loading } = useAuth();
    const navigate          = useNavigate();
    const fileRef           = useRef(null);
    const imageRef          = useRef(null);

    const [step,         setStep]         = useState(1);
    const [uploading,    setUploading]    = useState(false);
    const [submitting,   setSubmitting]   = useState(false);
    const [html,         setHtml]         = useState('');
    const [categories,   setCategories]   = useState([]);
    const [userPerms,    setUserPerms]    = useState([]);

    const [form, setForm] = useState({
        title:              '',
        excerpt:            '',
        category_id:        '',
        featured_image_url: '',
        author_name:        '',
        author_bio:         '',
        author_url:         '',
        author_photo_url:   '',
        author_organization:''
    });

    const isContributor = user?.role === 'contributor';
    const isLeader      = user?.role === 'group_leader';

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate('/login', { state: { from: { pathname: '/submit-article' } } }); return; }

        if (isAdmin) {
            api.get('/admin/my-permissions')
                .then(r => setUserPerms(r.data.permissions || []))
                .catch(() => {});
        }

        const hasAccess = isAdmin || isContributor || isLeader;
        if (!hasAccess) { navigate('/account'); return; }

        api.get('/articles/categories')
            .then(r => setCategories(r.data.categories || []))
            .catch(() => {});

        // Auto-fill contributor profile or user name
        api.get('/users/me/contributor')
            .then(r => {
                const p = r.data.profile;
                const fullName = user?.display_name || `${user?.first_name} ${user?.last_name}`;
                setForm(prev => ({
                    ...prev,
                    author_name:         (p?.display_name)     || fullName,
                    author_bio:          (p?.bio)              || prev.author_bio,
                    author_organization: (p?.organization)     || prev.author_organization,
                    author_url:          (p?.website_url)      || prev.author_url,
                    author_photo_url:    (p?.photo_url)        || prev.author_photo_url,
                    featured_image_url:  (p?.featured_image_url) || prev.featured_image_url
                }));
            })
            .catch(() => {
                if (user) {
                    setForm(prev => ({
                        ...prev,
                        author_name: user.display_name || `${user.first_name} ${user.last_name}`
                    }));
                }
            });
    }, [user, isAdmin, isContributor, loading, navigate]);

    // Wait for auth to load before rendering
    if (loading) return null;

    // Check if this admin can publish (needs articles permission)
    const canPublish = user?.role === 'superadmin' ||
        userPerms.includes('articles');

    // Check if this user can submit
    const canSubmit = isContributor || user?.role === 'superadmin' ||
        userPerms.includes('articles') ||
        userPerms.includes('submit_articles');

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await api.post('/articles/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setHtml(res.data.html);
            if (res.data.suggested_title && !form.title) {
                setForm(p => ({ ...p, title: res.data.suggested_title }));
            }
            toast.success('Document converted successfully!');
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to convert document');
        } finally {
            setUploading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);

        try {
            const res = await api.post('/articles/featured-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setForm(p => ({ ...p, featured_image_url: res.data.url }));
            toast.success('Image uploaded!');
        } catch {
            toast.error('Image upload failed');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(p => ({ ...p, [name]: value }));
    };

    const handleSubmit = async (publishNow = false) => {
        if (!form.title) { toast.error('Title required'); return; }
        if (!html)       { toast.error('No content to submit'); return; }

        setSubmitting(true);
        try {
            const res = await api.post('/articles/submit', {
                ...form,
                body_html:           html,
                publish_immediately: publishNow
            });
            toast.success(res.data.message);
            setStep(4);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Submission failed');
        } finally {
            setSubmitting(false);
        }
    };

    const iStyle = {
        width: '100%', background: '#f5f0e8',
        border: '0.5px solid #c8beaa', padding: '9px 12px',
        fontSize: '14px', fontFamily: 'sans-serif',
        color: '#1e2a3a', borderRadius: '3px',
        outline: 'none', boxSizing: 'border-box'
    };
    const lStyle = {
        display: 'block', fontSize: '11px',
        fontFamily: 'sans-serif', color: '#6a7080',
        letterSpacing: '0.08em', textTransform: 'uppercase',
        marginBottom: '5px'
    };

    return (
        <>
            <Helmet>
                <title>Submit Article — By Our Consent</title>
            </Helmet>

            {/* Header */}
            <div style={{ background: '#1e2a3a', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {isAdmin ? (
                    <Link to="/admin" style={{ color: '#b8ad98', fontSize: '12px', fontFamily: 'sans-serif', textDecoration: 'none', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        ← Back to admin
                    </Link>
                ) : (
                    <button onClick={() => navigate(-1)} style={{ background: 'transparent', border: 'none', color: '#b8ad98', fontSize: '12px', fontFamily: 'sans-serif', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase', padding: 0 }}>
                        ← Back
                    </button>
                )}
                <div style={{ color: '#e8dfc8', fontSize: '17px', fontWeight: 600, fontFamily: "'EB Garamond', Georgia, serif" }}>
                    Submit Article
                </div>
                <div style={{ width: '100px' }} />
            </div>

            {/* Step indicator */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '10px 1.5rem' }}>
                <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', gap: '0', alignItems: 'center' }}>
                    {['Upload file', 'Add details', 'Preview', 'Done'].map((label, i) => (
                        <React.Fragment key={label}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{
                                    width: '22px', height: '22px', borderRadius: '50%',
                                    background: step > i + 1 ? '#3b6d11' : step === i + 1 ? '#1e2a3a' : '#c8beaa',
                                    color: step >= i + 1 ? '#f5f0e8' : '#8a8070',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '11px', fontFamily: 'sans-serif', fontWeight: 600
                                }}>
                                    {step > i + 1 ? '✓' : i + 1}
                                </div>
                                <span style={{ fontSize: '12px', fontFamily: 'sans-serif', color: step === i + 1 ? '#1e2a3a' : '#8a8070', fontWeight: step === i + 1 ? 600 : 400 }}>
                                    {label}
                                </span>
                            </div>
                            {i < 3 && <div style={{ flex: 1, height: '0.5px', background: '#c8beaa', margin: '0 10px' }} />}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>

                {/* STEP 1 -- Upload file */}
                {step === 1 && (
                    <div>
                        <h1 style={{ fontSize: '26px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                            Upload your article
                        </h1>
                        <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '2rem', lineHeight: 1.6 }}>
                            Write your article in Word, Google Docs, or any word processor.
                            Save as <strong>.docx</strong> and upload here. We'll convert it automatically.
                        </p>

                        {/* Drop zone */}
                        <div
                            onClick={() => fileRef.current?.click()}
                            style={{
                                border: '2px dashed #c8beaa',
                                borderRadius: '4px',
                                padding: '3rem 2rem',
                                textAlign: 'center',
                                cursor: 'pointer',
                                background: '#f5f0e8',
                                transition: 'border-color 0.15s'
                            }}
                            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#8b1a1a'; }}
                            onDragLeave={e => { e.currentTarget.style.borderColor = '#c8beaa'; }}
                            onDrop={e => {
                                e.preventDefault();
                                e.currentTarget.style.borderColor = '#c8beaa';
                                const file = e.dataTransfer.files[0];
                                if (file) {
                                    const dt = new DataTransfer();
                                    dt.items.add(file);
                                    fileRef.current.files = dt.files;
                                    handleFileUpload({ target: { files: [file] } });
                                }
                            }}>
                            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
                            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>
                                {uploading ? 'Converting document...' : 'Drop your file here or click to browse'}
                            </div>
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                                Supports .docx, .doc, .txt, .rtf — max 10MB
                            </div>
                        </div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".docx,.doc,.txt,.rtf"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />

                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#ede8dc', borderRadius: '3px', border: '0.5px solid #c8beaa' }}>
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#5a6070', lineHeight: 1.7 }}>
                                <strong>Tips for best results:</strong><br />
                                • Use Heading 1/2/3 styles in Word for section headers<br />
                                • Keep formatting simple — bold, italic, lists work great<br />
                                • Images in the document will be stripped — upload a featured image in the next step<br />
                                • Remove headers/footers from the document before uploading
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 2 -- Metadata */}
                {step === 2 && (
                    <div>
                        <h1 style={{ fontSize: '26px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                            Article details
                        </h1>
                        <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '2rem' }}>
                            Fill in the details below. All fields except title are optional but recommended.
                        </p>

                        {/* Article info */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                                Article information
                            </div>

                            <label style={lStyle}>Title *</label>
                            <input style={{ ...iStyle, marginBottom: '1rem' }} name="title" value={form.title} onChange={handleChange} placeholder="Article title" />

                            <label style={lStyle}>Excerpt / summary</label>
                            <textarea style={{ ...iStyle, marginBottom: '1rem', resize: 'vertical' }} name="excerpt" value={form.excerpt} onChange={handleChange} rows={3} placeholder="Brief summary shown in article listings (1-2 sentences)" />

                            <label style={lStyle}>Category</label>
                            <select style={{ ...iStyle, marginBottom: '1rem' }} name="category_id" value={form.category_id} onChange={handleChange}>
                                <option value="">Select a category...</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Featured image */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.25rem', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem', paddingBottom: '8px', borderBottom: '0.5px solid #c8beaa' }}>
                                Featured image (optional)
                            </div>

                            {form.featured_image_url ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img src={`http://localhost:5002${form.featured_image_url}`} alt="Featured"
                                        style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '3px', border: '0.5px solid #c8beaa' }} />
                                    <div>
                                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#3b6d11', marginBottom: '4px' }}>✓ Image uploaded</div>
                                        <button onClick={() => setForm(p => ({ ...p, featured_image_url: '' }))}
                                            style={{ background: 'transparent', border: 'none', color: '#8b1a1a', fontSize: '11px', fontFamily: 'sans-serif', cursor: 'pointer', textDecoration: 'underline' }}>
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <button onClick={() => imageRef.current?.click()}
                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '8px 16px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                        Upload image
                                    </button>
                                    <span style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', marginLeft: '10px' }}>
                                        .jpg, .png — max 5MB — recommended 1200×630px
                                    </span>
                                    <input ref={imageRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                                </div>
                            )}
                        </div>

                        {/* Navigation */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                            <button onClick={() => setStep(1)}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                ← Back
                            </button>
                            <button onClick={() => setStep(3)}
                                style={{ background: '#1e2a3a', color: '#e8dfc8', border: 'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                                Preview article →
                            </button>
                        </div>
                    </div>
                )}

                {/* STEP 3 -- Preview */}
                {step === 3 && (
                    <div>
                        <h1 style={{ fontSize: '26px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                            Preview
                        </h1>
                        <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1.5rem' }}>
                            This is how your article will look. Review carefully before submitting.
                        </p>

                        {/* Article preview */}
                        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '2rem', marginBottom: '1.5rem' }}>

                            {/* Image left + title right layout */}
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                {form.featured_image_url && (
                                    <img src={`http://localhost:5002${form.featured_image_url}`} alt="Featured"
                                        style={{ width: '110px', height: '75px', objectFit: 'cover', borderRadius: '3px', flexShrink: 0 }} />
                                )}
                                <div style={{ flex: 1 }}>
                                    {form.category_id && (
                                        <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '6px' }}>
                                            {categories.find(c => c.id === form.category_id)?.name}
                                        </div>
                                    )}
                                    <h1 style={{ fontSize: '24px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.2, margin: 0 }}>
                                        {form.title || 'Untitled Article'}
                                    </h1>
                                </div>
                            </div>

                            {/* Author */}
                            {form.author_name && (
                                <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '0.5px solid #c8beaa' }}>
                                    By <strong>{form.author_name}</strong>
                                    {form.author_organization && ` @ ${form.author_organization}`}
                                    {form.author_url && (
                                        <> · <a href={form.author_url} target="_blank" rel="noopener noreferrer" style={{ color: '#8b6a3a' }}>{form.author_url.replace(/^https?:\/\//, '')}</a></>
                                    )}
                                </div>
                            )}

                            {/* Content */}
                            <div
                                style={{ fontSize: '16px', fontFamily: "'EB Garamond', Georgia, serif", color: '#2a3a2a', lineHeight: 1.8 }}
                                dangerouslySetInnerHTML={{ __html: html }}
                            />
                        </div>

                        {/* Submit buttons */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <button onClick={() => setStep(2)}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                ← Edit details
                            </button>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                {/* Save as draft (admin with articles permission only) */}
                                {canPublish && (
                                    <button onClick={() => handleSubmit(false)} disabled={submitting}
                                        style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                        Save as draft
                                    </button>
                                )}

                                {/* Publish now (articles permission) OR Submit for review (everyone else) */}
                                {canPublish ? (
                                    <button onClick={() => handleSubmit(true)} disabled={submitting}
                                        style={{ background: '#3b6d11', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                                        {submitting ? 'Publishing...' : 'Publish now'}
                                    </button>
                                ) : (
                                    <button onClick={() => handleSubmit(false)} disabled={submitting}
                                        style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 24px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                                        {submitting ? 'Submitting...' : 'Submit for review'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* STEP 4 -- Done */}
                {step === 4 && (
                    <div style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                        <div style={{ fontSize: '48px', marginBottom: '1rem' }}>
                            {isAdmin ? '✓' : '📬'}
                        </div>
                        <h1 style={{ fontSize: '26px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                            {isAdmin ? 'Article published!' : 'Article submitted for review'}
                        </h1>
                        <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', marginBottom: '2rem', lineHeight: 1.6 }}>
                            {isAdmin
                                ? 'Your article is now live on the site.'
                                : 'Thank you! Our editorial team will review your submission and you\'ll hear back by email.'}
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button onClick={() => { setStep(1); setHtml(''); setForm({ title:'', excerpt:'', category_id:'', featured_image_url:'', author_name:'', author_bio:'', author_url:'', author_photo_url:'', author_organization:'' }); }}
                                style={{ background: 'transparent', border: '0.5px solid #c8beaa', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', color: '#4a5060', cursor: 'pointer', borderRadius: '3px' }}>
                                Submit another
                            </button>
                        {isAdmin ? (
                            <Link to="/admin" style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                                Back to admin
                            </Link>
                        ) : (
                            <button onClick={() => navigate(-1)} style={{ background: '#8b1a1a', color: '#f5f0e8', border: 'none', padding: '10px 20px', fontSize: '12px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: '3px' }}>
                                Back to site
                            </button>
                        )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ArticleUploadPage;
